import * as THREE from "three";

function paint(color, extra = {}) {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.5,
    metalness: 0.03,
    flatShading: true,
    ...extra,
  });
}

function shade(hex, amount) {
  const color = new THREE.Color(hex);
  if (amount < 0) color.multiplyScalar(1 + amount);
  else color.lerp(new THREE.Color(0xffffff), amount);
  return color.getHex();
}

function part(geometry, material, x, y, z, extra = {}) {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(x, y, z);
  if (extra.rotation) mesh.rotation.set(...extra.rotation);
  if (extra.scale) mesh.scale.set(...extra.scale);
  mesh.castShadow = extra.cast !== false;
  mesh.receiveShadow = Boolean(extra.receive);
  return mesh;
}

export function paintRacer(mesh, color) {
  if (!mesh || !color || mesh.userData.color === color) return;
  const jacket = new THREE.Color(color);
  const dark = new THREE.Color(shade(color, -0.22));
  mesh.traverse((object) => {
    const role = object.material?.userData?.role;
    if (role === "jacket") object.material.color.copy(jacket);
    if (role === "jacket-dark") object.material.color.copy(dark);
  });
  mesh.userData.color = color;
}

export function createRacer(color = "#ff7b35", { parent } = {}) {
  const jacket = paint(color);
  jacket.userData.role = "jacket";
  const jacketDark = paint(shade(color, -0.22));
  jacketDark.userData.role = "jacket-dark";
  const skin = paint(0xffd4ab);
  const blush = paint(0xff9aad, { roughness: 0.75 });
  const navy = paint(0x1d3557);
  const boot = paint(0x243b53);
  const sole = paint(0xffe066);
  const vest = paint(0xfff4d1);
  const strap = paint(0x2c3d55);
  const eyeWhite = paint(0xfffdf8);
  const pupil = paint(0x1a2a3a);
  const ring = paint(0xffe14a, { emissive: 0x7a5200, emissiveIntensity: 0.16 });
  const ringStripe = paint(0xffffff);

  const root = new THREE.Group();
  const rig = new THREE.Group();
  root.add(rig);

  const torso = new THREE.Group();
  torso.position.y = 0.84;
  torso.add(part(new THREE.CapsuleGeometry(0.27, 0.42, 5, 10), jacket, 0, 0.02, 0));
  torso.add(part(new THREE.SphereGeometry(0.3, 10, 8), jacket, 0, -0.2, 0.02, { scale: [1.05, 0.5, 0.95] }));
  torso.add(part(new THREE.BoxGeometry(0.5, 0.44, 0.16), vest, 0, 0.05, 0.15));
  torso.add(part(new THREE.BoxGeometry(0.42, 0.4, 0.1), vest, 0, 0.04, -0.14));
  torso.add(part(new THREE.BoxGeometry(0.14, 0.07, 0.05), strap, -0.09, 0.04, 0.24));
  torso.add(part(new THREE.BoxGeometry(0.14, 0.07, 0.05), strap, 0.09, 0.04, 0.24));
  rig.add(torso);

  const floatie = new THREE.Group();
  floatie.position.y = 0.58;
  floatie.add(part(new THREE.TorusGeometry(0.4, 0.12, 8, 24), ring, 0, 0, 0, { rotation: [Math.PI / 2, 0, 0] }));
  floatie.add(part(new THREE.TorusGeometry(0.4, 0.045, 6, 24), ringStripe, 0, 0.02, 0, { rotation: [Math.PI / 2, 0, 0] }));
  rig.add(floatie);

  const head = new THREE.Group();
  head.position.set(0, 1.46, 0.05);
  head.add(part(new THREE.SphereGeometry(0.34, 14, 12), skin, 0, 0, 0));
  head.add(part(new THREE.SphereGeometry(0.065, 8, 6), blush, -0.2, -0.07, 0.24, { scale: [1, 0.65, 0.45], cast: false }));
  head.add(part(new THREE.SphereGeometry(0.065, 8, 6), blush, 0.2, -0.07, 0.24, { scale: [1, 0.65, 0.45], cast: false }));

  const hat = new THREE.Group();
  hat.position.y = 0.2;
  hat.add(part(new THREE.SphereGeometry(0.36, 14, 10, 0, Math.PI * 2, 0, Math.PI * 0.58), jacket, 0, 0.02, -0.02));
  hat.add(part(new THREE.CylinderGeometry(0.5, 0.5, 0.045, 20), jacketDark, 0, -0.04, -0.02));
  hat.add(part(new THREE.SphereGeometry(0.07, 8, 6), jacket, 0, 0.32, -0.04));
  head.add(hat);

  for (const side of [-1, 1]) {
    const eye = part(new THREE.SphereGeometry(0.08, 12, 10), eyeWhite, 0.125 * side, 0.03, 0.29, { scale: [1, 1.12, 0.72] });
    eye.add(part(new THREE.SphereGeometry(0.04, 10, 8), pupil, 0, 0.008, 0.045, { cast: false }));
    eye.add(part(new THREE.SphereGeometry(0.016, 6, 5), eyeWhite, 0.016, 0.022, 0.07, { cast: false }));
    head.add(eye);
    head.add(part(new THREE.BoxGeometry(0.09, 0.02, 0.03), navy, 0.125 * side, 0.145, 0.3, { rotation: [0, 0, -0.12 * side], cast: false }));
  }

  const smile = part(new THREE.TorusGeometry(0.075, 0.016, 6, 12, Math.PI), navy, 0, -0.11, 0.305, { cast: false });
  smile.rotation.z = Math.PI;
  head.add(smile);
  rig.add(head);

  function makeArm(side) {
    const arm = new THREE.Group();
    arm.position.set(0.3 * side, 1.08, 0);
    arm.rotation.z = 0.28 * side;
    arm.add(part(new THREE.CapsuleGeometry(0.07, 0.38, 4, 8), jacket, 0, -0.24, 0));
    arm.add(part(new THREE.SphereGeometry(0.08, 8, 6), skin, 0, -0.48, 0.01));
    rig.add(arm);
    return arm;
  }

  function makeLeg(side) {
    const leg = new THREE.Group();
    leg.position.set(0.12 * side, 0.62, 0);
    leg.add(part(new THREE.CapsuleGeometry(0.075, 0.26, 4, 8), jacketDark, 0, -0.18, 0));
    const bootMesh = part(new THREE.BoxGeometry(0.17, 0.15, 0.24), boot, 0, -0.38, 0.05);
    bootMesh.add(part(new THREE.BoxGeometry(0.19, 0.045, 0.26), sole, 0, -0.065, 0.01, { cast: false }));
    leg.add(bootMesh);
    rig.add(leg);
    return leg;
  }

  root.userData.body = torso;
  root.userData.rig = rig;
  root.userData.head = head;
  root.userData.floatie = floatie;
  root.userData.leftArm = makeArm(-1);
  root.userData.rightArm = makeArm(1);
  root.userData.leftLeg = makeLeg(-1);
  root.userData.rightLeg = makeLeg(1);
  root.userData.armHang = 0.28;
  root.userData.targetPosition = new THREE.Vector3();
  root.userData.color = color;
  root.visible = false;
  parent?.add(root);
  return root;
}

export function animateRunner(mesh, elapsed, speed = 0, grounded = true) {
  const { rig, head, floatie, leftArm, rightArm, leftLeg, rightLeg, armHang = 0.28 } = mesh.userData;
  if (!rig) return;
  const run = Math.min(1, Math.max(0, speed));
  rig.position.y = grounded ? Math.abs(Math.sin(elapsed * 10)) * run * 0.05 : 0.07;
  rig.rotation.z = Math.sin(elapsed * 9) * run * 0.05;

  const swing = grounded ? Math.sin(elapsed * 11) * run * 0.65 : 0.35;
  if (leftArm) {
    leftArm.rotation.z = -armHang;
    leftArm.rotation.x = grounded ? swing : -0.75;
  }
  if (rightArm) {
    rightArm.rotation.z = armHang;
    rightArm.rotation.x = grounded ? -swing : -0.75;
  }
  if (leftLeg) leftLeg.rotation.x = grounded ? -swing * 0.8 : 0.3;
  if (rightLeg) rightLeg.rotation.x = grounded ? swing * 0.8 : 0.5;
  if (head) {
    head.rotation.x = grounded ? Math.sin(elapsed * 7) * run * 0.04 : -0.1;
    head.rotation.y = Math.sin(elapsed * 2.8) * 0.08 * (0.35 + run);
  }
  if (floatie) {
    floatie.rotation.y = elapsed * 0.55;
    floatie.position.y = 0.58 + Math.sin(elapsed * 3.4) * 0.02;
  }
}
